import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import { SYNC_EXCLUDED_MODALITIES, SYNC_MODES } from "../../../../constants/Constants";
import {
  disableStackScrollSync,
  enableStackScrollSync,
} from "../../helpers/StackScrollSyncService";
import "../../styles/styles.css";

const LinkSVG = ({ active }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke={active ? "#0085FF" : "rgba(255, 255, 255, 0.65)"}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const ViewportSyncIndicator = ({ viewportIndex, viewportModality, lastSignaledTargetIndexRef }) => {
  const { t } = useTranslation();
  const {
    isSynchronizedEnabled,
    syncMode,
    activeViewportIndex,
    viewportSeriesData,
    linkedViewports,
    setLinkedViewports,
  } = useContext(ViewportContext);

  if (SYNC_EXCLUDED_MODALITIES.includes(viewportModality) || !isSynchronizedEnabled) {
    return null;
  }

  const isLinked = linkedViewports.includes(viewportIndex);

  const handleToggleLink = (e) => {
    e.stopPropagation();
    setLinkedViewports((prev) => {
      const wasLinked = prev.includes(viewportIndex);
      const next = wasLinked
        ? prev.filter((i) => i !== viewportIndex)
        : [...prev, viewportIndex];
      disableStackScrollSync();
      if (next.length > 0) {
        // Series data has settled — clear stale "last signalled" indices so the
        // first scroll after a drag-drop or sync re-enable always fires through
        // rather than being suppressed by a coincidentally matching index.
        lastSignaledTargetIndexRef.current = {};
        enableStackScrollSync(activeViewportIndex, syncMode, viewportSeriesData, next);
      } else {
        enableStackScrollSync(activeViewportIndex, syncMode, viewportSeriesData, null);
      }
      return next;
    });
  };

  return (
    // Match .viewport-overlay-sc-dicom's margin-right (0, relying on the parent
    // .viewport-overlay-right-column's 30px margin for scrollbar clearance) so
    // this row lines up directly under the SC-DICOM icon column above it.
    <div className="viewport-sync-link-wrapper" style={{ marginTop: "4px", marginRight: "0px", pointerEvents: "auto" }}>
      {syncMode === SYNC_MODES.MANUAL && (
        <span
          className="viewport-nav-icon"
          title={t("viewer.tools.link-series", "Link Series")}
          onClick={handleToggleLink}
        >
          <LinkSVG active={isLinked} />
        </span>
      )}

      {(syncMode === SYNC_MODES.AUTOMATIC || syncMode === SYNC_MODES.DISTANCE) && (
        <span
          className="viewport-nav-icon disabled"
          title={t("viewer.tools.link-series-disabled", "Linking controlled by sync mode")}
        >
          <LinkSVG active={false} />
        </span>
      )}
    </div>
  );
};

export default ViewportSyncIndicator;
